/**
 * Focus trap helpers for TRA overlays (modals, drawers, panels).
 * Presentation-only — no business logic.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * @param {ParentNode | null | undefined} root
 * @returns {HTMLElement[]}
 */
export function getFocusableElements(root) {
  if (!root || typeof root.querySelectorAll !== "function") return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    return true;
  });
}

/**
 * Activate a focus trap on `container` while `enabled` is true.
 * - Moves focus into the container on activate
 * - Cycles Tab / Shift+Tab inside
 * - Escape → onEscape
 * - Restores focus to the previously focused element on deactivate
 *
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {React.RefObject<HTMLElement | null>} options.containerRef
 * @param {() => void} [options.onEscape]
 * @param {boolean} [options.restoreFocus=true]
 * @param {boolean} [options.initialFocus=true]
 */
export function activateFocusTrap({
  enabled,
  containerRef,
  onEscape,
  restoreFocus = true,
  initialFocus = true,
}) {
  if (!enabled) return undefined;
  const container = containerRef?.current;
  if (!container) return undefined;

  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  if (initialFocus) {
    const nodes = getFocusableElements(container);
    if (nodes.length) {
      nodes[0].focus();
    } else {
      if (!container.hasAttribute("tabindex")) {
        container.setAttribute("tabindex", "-1");
      }
      container.focus();
    }
  }

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      if (typeof onEscape === "function") {
        event.preventDefault();
        event.stopPropagation();
        onEscape();
      }
      return;
    }

    if (event.key !== "Tab") return;

    const list = getFocusableElements(container);
    if (!list.length) {
      event.preventDefault();
      return;
    }

    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !container.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !container.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener("keydown", onKeyDown, true);

  return () => {
    document.removeEventListener("keydown", onKeyDown, true);
    if (
      restoreFocus &&
      previouslyFocused &&
      typeof previouslyFocused.focus === "function" &&
      document.contains(previouslyFocused)
    ) {
      previouslyFocused.focus();
    }
  };
}
